/**
 * AI Plan Customization — same layout modes as `CustomizeSheet`:
 * mobile bottom dock (non-blocking; outside tap closes via `useRefinementDockClickOutside`)
 * and desktop fixed rail for reading the plan while prompting.
 *
 * @see docs/project/specs/dynamic-template-system-v2-spec.md - Phase 4
 */

import { useCallback, useState } from 'react'
import {
  ActionIcon,
  Badge,
  Box,
  Button,
  Card,
  Collapse,
  Drawer,
  Group,
  List,
  Paper,
  ScrollArea,
  Stack,
  Text,
  Textarea,
  ThemeIcon,
  Title,
} from '@mantine/core'
import {
  IconCheck,
  IconClock,
  IconCurrencyDollar,
  IconMinus,
  IconPlus,
  IconRefresh,
  IconSend,
  IconSparkles,
  IconX,
} from '@tabler/icons-react'
import { useRefinementDockClickOutside } from '../hooks/useRefinementDockClickOutside'
import { REFINEMENT_SURFACE_Z_INDEX } from '../constants/refinement-layout'
import { useContentTier } from '../context/ContentTierContext'
import { RefinementRailPanel } from './RefinementRailPanel'
import type { PlanDiff, StepChange } from '../utils/plan-diff'

// Quick prompt suggestions
const QUICK_PROMPTS = [
  'Make this project simpler for a beginner',
  'I want to add heated floor',
  'What if I use vinyl instead of ceramic?',
  'Minimize the tools I need to buy',
  'Split this into weekend-sized chunks',
] as const

interface AiCustomizeSheetProps {
  opened: boolean
  onClose: () => void
  templateTitle: string
  templateSlug: string
  onSubmit: (prompt: string) => void
  isLoading?: boolean
  proposedDiff?: PlanDiff | null
  onAccept?: () => void
  onReject?: () => void
  usageRemaining?: number
  usageLimit?: number
  /** See `CustomizeSheet` — `rail` is used on large screens with parent padding. */
  layout?: 'drawer' | 'rail' | 'embedded'
}

export function AiCustomizeSheet({
  opened,
  onClose,
  templateTitle,
  templateSlug,
  onSubmit,
  isLoading = false,
  proposedDiff,
  onAccept,
  onReject,
  usageRemaining = 10,
  usageLimit = 10,
  layout = 'drawer',
}: AiCustomizeSheetProps) {
  const [prompt, setPrompt] = useState('')
  const { tier } = useContentTier()
  const [showDetails, setShowDetails] = useState(false)
  
  const isPremium = tier === 'premium'
  const isAuthenticated = tier !== 'anon'
  
  const handleUpgrade = useCallback(() => {
    onClose()
    // Navigate to sign-up with redirect back to this guide
    window.location.href = `/sign-up?redirect_url=/diy/${templateSlug}`
  }, [onClose, templateSlug])
  
  const handleSubmit = useCallback(() => {
    if (!prompt.trim() || isLoading) return
    onSubmit(prompt.trim())
    setPrompt('')
  }, [prompt, isLoading, onSubmit])
  
  const handleQuickPrompt = useCallback((p: string) => {
    onSubmit(p)
  }, [onSubmit])
  
  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey && prompt.trim() && !isLoading) {
      e.preventDefault()
      handleSubmit()
    }
  }, [prompt, isLoading, handleSubmit])
  
  const handleAccept = useCallback(() => {
    onAccept?.()
    setShowDetails(false)
  }, [onAccept])
  
  const handleReject = useCallback(() => {
    onReject?.()
    setShowDetails(false)
  }, [onReject])

  const dockPaddingBottom = 'calc(5.5rem + env(safe-area-inset-bottom, 0px))'

  const dockShellRef = useRefinementDockClickOutside(opened, onClose)

  const titleGroup = (
    <Group gap="xs" wrap="nowrap">
      <IconSparkles size={20} color="var(--mantine-color-primary-filled)" />
      <Text fw={500} size="sm" lineClamp={1}>
        AI customization
      </Text>
      {isPremium && (
        <Badge size="xs" color="yellow" variant="filled">
          Premium
        </Badge>
      )}
    </Group>
  )

  const stackBody = (
    <Stack
      p={layout === 'rail' || layout === 'embedded' ? 0 : 'md'}
      gap="md"
      pb={layout === 'drawer' ? dockPaddingBottom : undefined}
    >
        {/* Context */}
        <Paper p="sm" bg="var(--mantine-color-gray-light)" radius="sm">
          <Text size="sm" fw={500}>
            Customizing: {templateTitle}
          </Text>
          {isPremium && (
            <Text size="xs" c="dimmed" mt={4}>
              {usageRemaining} of {usageLimit} AI customizations remaining this month
            </Text>
          )}
        </Paper>
        
        {/* Gate for non-premium users */}
        {!isPremium && (
          <Card withBorder p="lg" bg="var(--mantine-color-gray-light)">
            <Stack gap="md" ta="center">
              <ThemeIcon size="xl" radius="xl" color="primary" variant="light">
                <IconSparkles size={24} />
              </ThemeIcon>
              <Title order={4}>Unlock AI Customization</Title>
              <Text size="sm" c="dimmed">
                {isAuthenticated
                  ? 'Upgrade to Premium to use AI-powered plan customization with natural language prompts.'
                  : 'Sign up and upgrade to Premium to use AI-powered plan customization.'}
              </Text>
              <Button
                leftSection={<IconSparkles size={18} />}
                onClick={handleUpgrade}
              >
                {isAuthenticated ? 'Upgrade to Premium' : 'Get Started'}
              </Button>
            </Stack>
          </Card>
        )}
        
        {/* Premium content */}
        {isPremium && (
          <>
            {/* Proposed diff preview */}
            {proposedDiff && !proposedDiff.isEmpty && (
              <Card withBorder p="md" bg="var(--mantine-color-blue-light)">
                <Group justify="space-between" mb="sm">
                  <Group gap="xs">
                    <IconSparkles size={18} color="var(--mantine-color-blue-filled)" />
                    <Text fw={500}>Proposed Changes</Text>
                  </Group>
                  <Button
                    variant="subtle"
                    size="xs"
                    onClick={() => setShowDetails(!showDetails)}
                  >
                    {showDetails ? 'Hide Details' : 'Show Details'}
                  </Button>
                </Group>
                
                {/* Summary stats */}
                <Group gap="lg" mb="sm">
                  {proposedDiff.stepsAdded > 0 && (
                    <Group gap={4}>
                      <IconPlus size={14} color="var(--mantine-color-green-6)" />
                      <Text size="sm">{proposedDiff.stepsAdded} steps added</Text>
                    </Group>
                  )}
                  {proposedDiff.stepsRemoved > 0 && (
                    <Group gap={4}>
                      <IconMinus size={14} color="var(--mantine-color-red-6)" />
                      <Text size="sm">{proposedDiff.stepsRemoved} steps removed</Text>
                    </Group>
                  )}
                  {proposedDiff.stepsModified > 0 && (
                    <Group gap={4}>
                      <IconRefresh size={14} color="var(--mantine-color-blue-6)" />
                      <Text size="sm">{proposedDiff.stepsModified} steps modified</Text>
                    </Group>
                  )}
                  {proposedDiff.timeDelta !== 0 && (
                    <Group gap={4}>
                      <IconClock size={14} color="var(--mantine-color-dimmed)" />
                      <Text size="sm">
                        {proposedDiff.timeDelta > 0 ? '+' : ''}
                        {proposedDiff.timeDelta}h
                      </Text>
                    </Group>
                  )}
                  {(proposedDiff.costDeltaMin !== 0 || proposedDiff.costDeltaMax !== 0) && (
                    <Group gap={4}>
                      <IconCurrencyDollar size={14} color="var(--mantine-color-dimmed)" />
                      <Text size="sm">
                        {proposedDiff.costDeltaMin > 0 ? '+' : ''}
                        ${Math.round((proposedDiff.costDeltaMin + proposedDiff.costDeltaMax) / 2)}
                      </Text>
                    </Group>
                  )}
                </Group>
                
                {/* Detailed changes */}
                <Collapse in={showDetails}>
                  <ScrollArea h={150} offsetScrollbars>
                    <List size="sm" spacing="xs">
                      {proposedDiff.stepChanges.map((change, idx) => (
                        <List.Item
                          key={`${change.stepSlug}-${idx}`}
                          icon={<StepChangeIcon type={change.type} />}
                        >
                          <Text size="sm">
                            <Text span fw={500}>{change.stepTitle}</Text>
                            {change.details && (
                              <Text span c="dimmed"> — {change.details}</Text>
                            )}
                          </Text>
                        </List.Item>
                      ))}
                    </List>
                  </ScrollArea>
                </Collapse>
                
                {/* Accept/Reject buttons */}
                <Group mt="md">
                  <Button
                    leftSection={<IconCheck size={18} />}
                    onClick={handleAccept}
                    flex={1}
                  >
                    Accept Changes
                  </Button>
                  <Button
                    variant="light"
                    leftSection={<IconX size={18} />}
                    onClick={handleReject}
                    flex={1}
                  >
                    Reject
                  </Button>
                </Group>
              </Card>
            )}
            
            {/* Loading state */}
            {isLoading && (
              <Card withBorder p="md">
                <Group gap="sm">
                  <IconSparkles
                    size={20}
                    color="var(--mantine-color-primary-filled)"
                    style={{ animation: 'pulse 1.5s infinite' }}
                  />
                  <Text size="sm" c="dimmed" fs="italic">
                    Analyzing your request and computing changes...
                  </Text>
                </Group>
              </Card>
            )}
            
            {/* Quick prompts - hide when showing diff */}
            {!proposedDiff && !isLoading && (
              <Box>
                <Text size="xs" c="dimmed" mb="xs">
                  Try these suggestions:
                </Text>
                <Group gap="xs" wrap="wrap">
                  {QUICK_PROMPTS.map((p) => (
                    <Badge
                      key={p}
                      variant="light"
                      color="primary"
                      style={{ cursor: 'pointer' }}
                      onClick={() => handleQuickPrompt(p)}
                    >
                      {p}
                    </Badge>
                  ))}
                </Group>
              </Box>
            )}
            
            {/* Custom prompt input */}
            <Textarea
              placeholder="Describe how you'd like to modify this plan..."
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              onKeyDown={handleKeyDown}
              minRows={2}
              maxRows={4}
              autosize
              disabled={isLoading || !!proposedDiff}
            />
            
            <Button
              fullWidth
              leftSection={<IconSend size={18} />}
              onClick={handleSubmit}
              loading={isLoading}
              disabled={!prompt.trim() || isLoading || !!proposedDiff}
            >
              {proposedDiff ? 'Review changes above' : 'Customize Plan'}
            </Button>
          </>
        )}
    </Stack>
  )

  if (layout === 'embedded') {
    if (!opened) return null
    return <Box className="ai-customize-sheet-embedded">{stackBody}</Box>
  }

  if (layout === 'rail') {
    if (!opened) return null
    return (
      <RefinementRailPanel
        ariaLabel="AI plan customization"
        title="AI customization"
        headerPrefix={
          <IconSparkles size={20} color="var(--mantine-color-primary-filled)" />
        }
        headerSuffix={
          isPremium ? (
            <Badge size="xs" color="yellow" variant="filled">
              Premium
            </Badge>
          ) : undefined
        }
        onClose={onClose}
        closeAriaLabel="Close AI customization"
      >
        {stackBody}
      </RefinementRailPanel>
    )
  }

  return (
    <Drawer
      opened={opened}
      onClose={onClose}
      position="bottom"
      size="auto"
      zIndex={REFINEMENT_SURFACE_Z_INDEX}
      title={null}
      withCloseButton={false}
      withOverlay={false}
      lockScroll={false}
      trapFocus={false}
      closeOnClickOutside={false}
      styles={{
        content: {
          borderTopLeftRadius: 'var(--mantine-radius-lg)',
          borderTopRightRadius: 'var(--mantine-radius-lg)',
          maxHeight: 'min(68dvh, 520px)',
        },
        body: { padding: 0 },
      }}
    >
      <Box ref={dockShellRef}>
        <Group justify="space-between" align="center" wrap="nowrap" gap="xs" px="md" pt="sm">
          <Box style={{ flex: 1, minWidth: 0 }}>{titleGroup}</Box>
          <Drawer.CloseButton aria-label="Close AI customization" />
        </Group>
        <ScrollArea mah="min(60dvh, 480px)" offsetScrollbars type="auto">
          {stackBody}
        </ScrollArea>
      </Box>
    </Drawer>
  )
}

function StepChangeIcon({ type }: { type: StepChange['type'] }) {
  switch (type) {
    case 'added':
      return (
        <ThemeIcon size="sm" radius="xl" color="green" variant="light">
          <IconPlus size={12} />
        </ThemeIcon>
      )
    case 'removed':
      return (
        <ThemeIcon size="sm" radius="xl" color="red" variant="light">
          <IconMinus size={12} />
        </ThemeIcon>
      )
    case 'modified':
      return (
        <ThemeIcon size="sm" radius="xl" color="blue" variant="light">
          <IconRefresh size={12} />
        </ThemeIcon>
      )
  }
}
